import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppState, Booking, DisabledPrevButtonType } from './appTypes';
import { Payload, TimeSlots } from '../../services/Appointment/model';
import { services } from '../../context';
import { SlotsObjects } from '../timeslots/timeSlotsTypes';
import { getInitialSessionOnClientEntry } from '../../helpers/sessionHelper';

const DEFAULT_BOOKING: Booking = {
  orderNo: '',
  questionsToken: '',
  payOrderLink: '',
  payOrderLinkEnabled: true,
};

const initialState = (): AppState => {
  const restoredState = getInitialSessionOnClientEntry();
  return {
    isLoading: true,
    disabledPrevButton: restoredState.disabledPrevButton,
    operation: '',
    booking: restoredState.booking || DEFAULT_BOOKING,
    selected: {
      ...restoredState.selected,
    },
  };
};

const timeSlotExtractor = (
  timeSlots: SlotsObjects,
  timeSlotId: string,
  storeId: string,
): TimeSlots | undefined => {
  if (!timeSlotId) return undefined;

  const allTimeSlotsOnGivenDate = timeSlots[storeId].find((slots) =>
    slots.timeSlots.some((slot) => slot.id === timeSlotId),
  );
  return allTimeSlotsOnGivenDate?.timeSlots.find(
    (slot) => slot.id === timeSlotId,
  );
};

export const confirmAppointment = createAsyncThunk(
  'app/confirmAppointment',
  async (
    {
      zipCode,
      serviceId,
      storeId,
      timeSlotId,
      from,
      serviceCode,
      payload,
    }: {
      zipCode: string;
      serviceId: string;
      storeId: string;
      timeSlotId: string;
      from: string;
      serviceCode: string;
      payload: Payload;
    },
    { rejectWithValue },
  ) => {
    const { result, error } = await services.appointment.confirmAppointment(
      zipCode,
      serviceId,
      storeId,
      timeSlotId,
      from,
      serviceCode,
      payload,
    );

    if (error) {
      return rejectWithValue(error);
    }

    return {
      orderNo: result.orderNo as string,
      payOrderLink: result.payOrderLink as string,
      questionsToken: result.questionsToken as string,
      payOrderLinkEnabled: result.payOrderLinkEnabled as boolean,
    };
  },
);

const appSlice = createSlice({
  name: 'app',
  initialState: initialState(),
  reducers: {
    updateLoadingState(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    updateSelectedService(
      state,
      action: PayloadAction<
        { serviceId: string; serviceCode: string } | undefined
      >,
    ) {
      state.selected.serviceId = action.payload?.serviceId || '';
      state.selected.serviceCode = action.payload?.serviceCode || '';
    },
    updateSelectedStore(state, action: PayloadAction<string>) {
      state.selected.storeId = action.payload;
      state.selected.timeSlot = null;
    },
    updateSelectedTimeSlot(
      state,
      action: PayloadAction<{
        timeSlots: SlotsObjects;
        id: string;
        storeId: string;
      }>,
    ) {
      state.selected.timeSlot =
        timeSlotExtractor(
          action.payload.timeSlots,
          action.payload.id,
          action.payload.storeId,
        ) || null;
    },
    clearSelected(state) {
      state.selected = {
        serviceId: '',
        serviceCode: '',
        storeId: 'default',
        timeSlot: null,
      };
    },
    clearSelectedTimeSlot(state) {
      state.selected = {
        ...state.selected,
        timeSlot: null,
      };
    },
    clearBooking(state) {
      state.booking = DEFAULT_BOOKING;
    },
    setOperation(state, action: PayloadAction<string>) {
      state.operation = action.payload;
    },
    setDisabledPrevButton(
      state,
      action: PayloadAction<DisabledPrevButtonType>,
    ) {
      state.disabledPrevButton = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(confirmAppointment.fulfilled, (state, action) => {
      state.booking.orderNo = action.payload.orderNo;
      state.booking.payOrderLink = action.payload.payOrderLink;
      state.booking.questionsToken = action.payload.questionsToken;
      state.booking.payOrderLinkEnabled = action.payload.payOrderLinkEnabled;
    });
  },
});

export const {
  updateLoadingState,
  updateSelectedService,
  updateSelectedStore,
  updateSelectedTimeSlot,
  clearSelectedTimeSlot,
  clearSelected,
  clearBooking,
  setOperation,
  setDisabledPrevButton,
} = appSlice.actions;

export default appSlice.reducer;
